import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnDestroy, Output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { StatusCardWorkspaceService } from '../../shared-services/status-card-workspace.service';

interface DetailFormOption {
  label?: string;
  value?: string;
}

interface DetailFormField {
  type?: string;
  label?: string;
  value?: unknown;
  placeholder?: string;
  options?: DetailFormOption[];
  checked?: boolean;
  disabled?: boolean;
  width?: string;
  variant?: string;
  action?: string;
  toggleValues?: string[];
  icon?: string;
  linkedTag?: string;
  showWhenTag?: string;
  layout?: string;
  min?: number;
  max?: number;
  step?: number;
}

interface DetailFormRow {
  removable?: boolean;
  draggable?: boolean;
  expanded?: boolean;
  fields?: DetailFormField[];
  expandedFields?: DetailFormField[];
  validValues?: string[];
}

interface DetailFormSection {
  variant?: string;
  emptyText?: string;
  title?: string;
  heading?: string;
  label?: string;
  name?: string;
  subtitle?: string;
  count?: string | number;
  icon?: string;
  accent?: string;
  collapsed?: boolean;
  headerFields?: DetailFormField[];
  search?: {
    placeholder?: string;
  };
  columns?: Array<{
    label?: string;
  }>;
  fields?: DetailFormField[];
  rows?: DetailFormRow[];
  rowDetailFields?: DetailFormField[];
  rowValidValues?: string[];
  draggableRows?: boolean;
  addRow?: {
    fields?: DetailFormField[];
  };
  actions?: DetailFormField[];
}

interface DeamReadFilter {
  name: string;
  type: string;
  sql: string;
  expanded: boolean;
}

@Component({
  selector: 'app-configurable-detail-form',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './configurable-detail-form.component.html',
  styleUrl: './configurable-detail-form.component.scss',
})
export class ConfigurableDetailFormComponent implements OnDestroy {
  private readonly workspaceService = inject(StatusCardWorkspaceService);
  private readonly http = inject(HttpClient);
  private workspaceSubscription?: Subscription;
  private templateSubscription?: Subscription;
  private workspaceSource = '';
  private loadedConfigUrl = '';
  private _data: any;
  @Input() set data(value: any) {
    this._data = value;
    this.bindWorkspace(value);
    this.loadTemplateConfig(value);
  }
  get data(): any {
    return this._data;
  }
  @Input() contextTitle = '';
  @Input() context: Record<string, any> | null = null;
  @Output() statusChange = new EventEmitter<string>();
  @Output() actionTriggered = new EventEmitter<string>();
  @Output() toggleChange = new EventEmitter<{ tag: string; checked: boolean }>();
  @Output() contentChanged = new EventEmitter<void>();
  private controlState = new Map<string, boolean>();
  private collapsedState = new Map<number, boolean>();
  private expandedRowState = new Map<string, boolean>();
  private addFieldState = new Map<string, string>();
  private rowFieldState = new Map<string, string>();
  private childEntityState = new Map<string, string[]>();
  private interEntityState = new Map<string, string[]>();
  private deamReadState = new Map<string, DeamReadFilter[]>();
  private deamReadInputState = new Map<string, { name: string; type: string }>();
  private draggedRow:
    | {
        sectionIndex: number;
        rowIndex: number;
      }
    | null = null;

  private bindWorkspace(value: any): void {
    const source = String(value?.binding?.source ?? '').trim();
    if (!source || source === this.workspaceSource) return;
    this.workspaceSubscription?.unsubscribe();
    this.workspaceSource = source;
    this.workspaceSubscription = this.workspaceService.state$(source).subscribe((state) => {
      if (!state) return;
      if (state.detailPanel) this._data = state.detailPanel;
      this.context = state.item;
      this.contextTitle = String(state.item?.title ?? '');
    });
  }

  private loadTemplateConfig(value: any): void {
    const configUrl = String(value?.configUrl ?? '').trim();
    if (!configUrl || configUrl === this.loadedConfigUrl) return;
    this.loadedConfigUrl = configUrl;
    this.templateSubscription?.unsubscribe();
    this.templateSubscription = this.http.get<any>(configUrl).subscribe({
      next: (config) => {
        const columns = (config?.pageContent || []).flatMap((row: any) => row?.columns || []);
        const sourceWidget = columns.find((column: any) =>
          column?.widgetId === value?.detailSourceWidgetId || column?.type === 'status-card-list');
        const templateTitle = String(sourceWidget?.detailPanelTemplateItem ?? '').trim();
        const templateItem = (sourceWidget?.items || []).find((item: any) => item?.title === templateTitle);
        const template = templateItem?.detailPanel || sourceWidget?.detailPanel;
        if (template && !this.headerFields.length && !this.sections.length) this._data = template;
      },
    });
  }

  ngOnDestroy(): void {
    this.workspaceSubscription?.unsubscribe();
    this.templateSubscription?.unsubscribe();
  }

  private notifyContentChanged(): void {
    this.contentChanged.emit();
    if (this.workspaceSource) {
      this.workspaceService.dispatch(this.workspaceSource, { type: 'detailChanged' });
    }
  }

  get headerFields(): DetailFormField[] {
    return Array.isArray(this.data?.headerFields) ? this.data.headerFields : [];
  }

  get sections(): DetailFormSection[] {
    return Array.isArray(this.data?.sections) ? this.data.sections : [];
  }

  fieldStyle(field: DetailFormField): Record<string, string> {
    return {
      '--field-width': field.width || 'minmax(180px, 1fr)',
    };
  }

  sectionAccent(section: DetailFormSection): Record<string, string> {
    const accent = String(section.accent || '').trim();
    const isDefaultNavy = !accent || accent.toLowerCase() === '#043d63';
    return {
      '--section-accent': isDefaultNavy ? 'var(--dvm-btn-primary-bg, #043D63)' : accent,
    };
  }

  text(value: unknown): string {
    const templateContext = { title: this.contextTitle, ...(this.context ?? {}) };
    return String(value ?? '').replace(/{{\s*([\w.]+)\s*}}/g, (_match, path: string) => {
      const resolved = path.split('.').reduce<any>((current, key) => current?.[key], templateContext);
      return resolved === undefined || resolved === null ? '' : String(resolved);
    });
  }

  fieldVisible(field: DetailFormField): boolean {
    if (!field.showWhenTag) return true;
    return this.contextHasTag(field.showWhenTag);
  }

  setFieldValue(field: DetailFormField, event: Event): void {
    field.value = (event.target as HTMLInputElement | HTMLSelectElement | null)?.value ?? '';
    this.notifyContentChanged();
  }

  toggleBadge(field: DetailFormField): void {
    const values = Array.isArray(field.toggleValues) ? field.toggleValues.filter(Boolean) : [];
    if (values.length < 2) return;

    const current = this.text(field.value);
    const currentIndex = values.findIndex((value) => value.toLowerCase() === current.toLowerCase());
    const status = values[(currentIndex + 1) % values.length];
    field.value = status;
    this.statusChange.emit(status);
    if (this.workspaceSource) this.workspaceService.dispatch(this.workspaceSource, { type: 'statusChange', payload: status });
    this.notifyContentChanged();
  }

  triggerAction(action: string): void {
    this.actionTriggered.emit(action);
    if (this.workspaceSource) this.workspaceService.dispatch(this.workspaceSource, { type: 'detailAction', payload: action });
  }

  sectionTitle(section: DetailFormSection): string {
    return this.text(section.title || section.heading || section.label || section.name || '');
  }

  isCollapsed(section: DetailFormSection, sectionIndex: number): boolean {
    return this.collapsedState.has(sectionIndex)
      ? !!this.collapsedState.get(sectionIndex)
      : !!section.collapsed;
  }

  toggleSection(section: DetailFormSection, sectionIndex: number): void {
    this.collapsedState.set(sectionIndex, !this.isCollapsed(section, sectionIndex));
  }

  rowKey(sectionIndex: number, rowIndex: number): string {
    return `${sectionIndex}:${rowIndex}`;
  }

  rowExpandedFields(section: DetailFormSection, row: DetailFormRow): DetailFormField[] {
    if (Array.isArray(row.expandedFields) && row.expandedFields.length) {
      return row.expandedFields;
    }

    return Array.isArray(section.rowDetailFields) ? section.rowDetailFields : [];
  }

  rowValidValues(section: DetailFormSection, row: DetailFormRow): string[] {
    if (Array.isArray(row.validValues)) {
      return row.validValues;
    }

    return Array.isArray(section.rowValidValues) ? section.rowValidValues : [];
  }

  isRowExpandable(section: DetailFormSection, row: DetailFormRow): boolean {
    return this.rowExpandedFields(section, row).length > 0;
  }

  sectionHasExpandableRows(section: DetailFormSection): boolean {
    return Array.isArray(section.rows) && section.rows.some((row) => this.isRowExpandable(section, row));
  }

  isRowExpanded(row: DetailFormRow, sectionIndex: number, rowIndex: number): boolean {
    const key = this.rowKey(sectionIndex, rowIndex);
    return this.expandedRowState.has(key) ? !!this.expandedRowState.get(key) : !!row.expanded;
  }

  toggleRow(section: DetailFormSection, row: DetailFormRow, sectionIndex: number, rowIndex: number): void {
    if (!this.isRowExpandable(section, row)) {
      return;
    }

    this.expandedRowState.set(
      this.rowKey(sectionIndex, rowIndex),
      !this.isRowExpanded(row, sectionIndex, rowIndex),
    );
  }

  isRowDraggable(section: DetailFormSection, row: DetailFormRow): boolean {
    return !!section.draggableRows || !!row.draggable;
  }

  onDragStart(sectionIndex: number, rowIndex: number, event: DragEvent): void {
    this.draggedRow = { sectionIndex, rowIndex };
    event.dataTransfer?.setData('text/plain', `${sectionIndex}:${rowIndex}`);
    if (event.currentTarget instanceof Element) {
      event.dataTransfer?.setDragImage(event.currentTarget, 16, 16);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  onDrop(section: DetailFormSection, sectionIndex: number, targetRowIndex: number, event: DragEvent): void {
    event.preventDefault();

    if (!this.draggedRow || this.draggedRow.sectionIndex !== sectionIndex || !section.rows) {
      this.draggedRow = null;
      return;
    }

    const sourceRowIndex = this.draggedRow.rowIndex;
    if (sourceRowIndex === targetRowIndex) {
      this.draggedRow = null;
      return;
    }

    const [movedRow] = section.rows.splice(sourceRowIndex, 1);
    section.rows.splice(targetRowIndex, 0, movedRow);
    this.expandedRowState.clear();
    this.draggedRow = null;
  }

  removeRow(section: DetailFormSection, rowIndex: number): void {
    if (!Array.isArray(section.rows)) {
      return;
    }

    section.rows.splice(rowIndex, 1);
    this.expandedRowState.clear();
  }

  controlKey(sectionIndex: number, group: string, rowIndex: number, fieldIndex: number): string {
    return `${sectionIndex}:${group}:${rowIndex}:${fieldIndex}`;
  }

  isChecked(field: DetailFormField, sectionIndex: number, group: string, rowIndex: number, fieldIndex: number): boolean {
    if (field.linkedTag) return this.contextHasTag(field.linkedTag);
    const key = this.controlKey(sectionIndex, group, rowIndex, fieldIndex);
    return this.controlState.has(key) ? !!this.controlState.get(key) : !!field.checked;
  }

  toggleControl(field: DetailFormField, sectionIndex: number, group: string, rowIndex: number, fieldIndex: number): void {
    if (field.disabled) {
      return;
    }

    const key = this.controlKey(sectionIndex, group, rowIndex, fieldIndex);
    const checked = !this.isChecked(field, sectionIndex, group, rowIndex, fieldIndex);
    field.checked = checked;
    this.controlState.set(key, checked);
    if (field.linkedTag) {
      const change = { tag: field.linkedTag, checked };
      this.toggleChange.emit(change);
      if (this.workspaceSource) this.workspaceService.dispatch(this.workspaceSource, { type: 'toggleChange', payload: change });
    }
    this.notifyContentChanged();
  }

  private contextHasTag(tag: string): boolean {
    const tags = Array.isArray(this.context?.['tags']) ? this.context?.['tags'] : [];
    return tags.some((item: any) => String(item?.label ?? item).toLowerCase() === tag.toLowerCase());
  }

  addFieldKey(sectionIndex: number, fieldIndex: number): string {
    return `${sectionIndex}:add:${fieldIndex}`;
  }

  addFieldValue(sectionIndex: number, fieldIndex: number, field: DetailFormField): string {
    const key = this.addFieldKey(sectionIndex, fieldIndex);
    return this.addFieldState.has(key) ? this.addFieldState.get(key) || '' : this.text(field.value);
  }

  setAddFieldValue(sectionIndex: number, fieldIndex: number, event: Event): void {
    const target = event.target as HTMLInputElement | HTMLSelectElement | null;
    this.addFieldState.set(this.addFieldKey(sectionIndex, fieldIndex), target?.value || '');
  }

  rowFieldValue(sectionIndex: number, rowIndex: number, fieldIndex: number, field: DetailFormField): string {
    const key = `${this.contextTitle}:${sectionIndex}:${rowIndex}:${fieldIndex}`;
    return this.rowFieldState.has(key) ? this.rowFieldState.get(key) || '' : this.text(field.value);
  }

  setRowFieldValue(sectionIndex: number, rowIndex: number, fieldIndex: number, event: Event): void {
    const target = event.target as HTMLInputElement | HTMLSelectElement | null;
    this.rowFieldState.set(`${this.contextTitle}:${sectionIndex}:${rowIndex}:${fieldIndex}`, target?.value || '');
  }

  addInlineRow(section: DetailFormSection, sectionIndex: number, row: DetailFormRow, rowIndex: number): void {
    const fields = (row.fields || []).filter((field) => field.type !== 'button');
    const values = fields.map((field, fieldIndex) => this.rowFieldValue(sectionIndex, rowIndex, fieldIndex, field));
    if (values.some((value) => !value.trim())) return;
    const addedRow: DetailFormRow = {
      removable: true,
      fields: fields.map((field, fieldIndex) => ({ ...field, value: values[fieldIndex], placeholder: '' })),
    };
    section.rows = section.rows || [];
    section.rows.splice(Math.max(0, section.rows.length - 1), 0, addedRow);
    this.updateBusinessRuleSummary(section);
  }

  ruleRows(section: DetailFormSection): DetailFormRow[] {
    return (section.rows || []).filter((row) => row.removable);
  }

  ruleInputRow(section: DetailFormSection): DetailFormRow {
    return (section.rows || []).find((row) => !row.removable) || { fields: [] };
  }

  removeBusinessRule(section: DetailFormSection, row: DetailFormRow): void {
    section.rows = (section.rows || []).filter((item) => item !== row);
    this.updateBusinessRuleSummary(section);
  }

  interEntities(): string[] {
    return this.interEntityState.get(this.contextTitle) || [];
  }

  interEntityInput(sectionIndex: number): string {
    return this.rowFieldState.get(`${this.contextTitle}:${sectionIndex}:inter-entity`) || '';
  }

  setInterEntityInput(sectionIndex: number, event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.rowFieldState.set(`${this.contextTitle}:${sectionIndex}:inter-entity`, target?.value || '');
  }

  addInterEntity(section: DetailFormSection, sectionIndex: number): void {
    const value = this.interEntityInput(sectionIndex).trim();
    if (!value) return;
    const entities = this.interEntities();
    if (!entities.some((item) => item.toLowerCase() === value.toLowerCase())) {
      this.interEntityState.set(this.contextTitle, [...entities, value]);
    }
    this.rowFieldState.set(`${this.contextTitle}:${sectionIndex}:inter-entity`, '');
    this.updateBusinessRuleSummary(section);
  }

  removeInterEntity(section: DetailFormSection, value: string): void {
    this.interEntityState.set(this.contextTitle, this.interEntities().filter((item) => item !== value));
    this.updateBusinessRuleSummary(section);
  }

  totalBusinessRules(section: DetailFormSection): number {
    return this.ruleRows(section).length + this.interEntities().length;
  }

  private updateBusinessRuleSummary(section: DetailFormSection): void {
    section.subtitle = `${this.totalBusinessRules(section)} rules`;
  }

  deamReads(): DeamReadFilter[] {
    return this.deamReadState.get(this.contextTitle) || [];
  }

  deamReadInput(): { name: string; type: string } {
    return this.deamReadInputState.get(this.contextTitle) || { name: '', type: 'static' };
  }

  setDeamReadInput(key: 'name' | 'type', event: Event): void {
    const target = event.target as HTMLInputElement | HTMLSelectElement | null;
    this.deamReadInputState.set(this.contextTitle, { ...this.deamReadInput(), [key]: target?.value || '' });
  }

  addDeamRead(section: DetailFormSection): void {
    const input = this.deamReadInput();
    const name = input.name.trim();
    if (!name) return;
    this.deamReadState.set(this.contextTitle, [
      ...this.deamReads(),
      { name, type: input.type || 'static', sql: '', expanded: false },
    ]);
    this.deamReadInputState.set(this.contextTitle, { name: '', type: 'static' });
    this.updateDeamReadSummary(section);
  }

  removeDeamRead(section: DetailFormSection, filter: DeamReadFilter): void {
    this.deamReadState.set(this.contextTitle, this.deamReads().filter((item) => item !== filter));
    this.updateDeamReadSummary(section);
  }

  setDeamReadType(filter: DeamReadFilter, event: Event): void {
    filter.type = (event.target as HTMLSelectElement | null)?.value || 'static';
  }

  setDeamReadSql(filter: DeamReadFilter, event: Event): void {
    filter.sql = (event.target as HTMLTextAreaElement | null)?.value || '';
  }

  private updateDeamReadSummary(section: DetailFormSection): void {
    section.count = this.deamReads().length;
    section.subtitle = `${section.count} ${section.count === 1 ? 'filter' : 'filters'}`;
  }

  childEntities(): string[] {
    return this.childEntityState.get(this.contextTitle) || [];
  }

  childInputValue(sectionIndex: number): string {
    return this.rowFieldState.get(`${this.contextTitle}:${sectionIndex}:child`) || '';
  }

  setChildValue(sectionIndex: number, event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.rowFieldState.set(`${this.contextTitle}:${sectionIndex}:child`, target?.value || '');
  }

  linkChild(section: DetailFormSection, sectionIndex: number): void {
    const value = this.childInputValue(sectionIndex).trim();
    if (!value) return;
    const values = this.childEntities();
    if (!values.some((item) => item.toLowerCase() === value.toLowerCase())) {
      this.childEntityState.set(this.contextTitle, [...values, value]);
    }
    this.rowFieldState.set(`${this.contextTitle}:${sectionIndex}:child`, '');
    section.count = this.childEntities().length;
    section.subtitle = this.childEntities().join(', ') || 'none';
  }

  removeChild(section: DetailFormSection, value: string): void {
    this.childEntityState.set(this.contextTitle, this.childEntities().filter((item) => item !== value));
    section.count = this.childEntities().length;
    section.subtitle = this.childEntities().join(', ') || 'none';
  }

  addRow(section: DetailFormSection, sectionIndex: number): void {
    const addFields = section.addRow?.fields || [];
    const valueFields = addFields
      .map((field, fieldIndex) => ({ field, fieldIndex, value: this.addFieldValue(sectionIndex, fieldIndex, field).trim() }))
      .filter(({ field }) => field.type !== 'button');

    const typeValue = valueFields.find(({ field }) => field.type === 'select')?.value || 'string';
    const textValues = valueFields.filter(({ field }) => field.type === 'text').map(({ value }) => value);
    const nameValue = textValues[0];

    if (!nameValue) {
      return;
    }

    const displayValue = textValues[1] || nameValue;
    section.rows = section.rows || [];
    section.rows.push({
      draggable: true,
      fields: [
        {
          type: 'badge',
          value: typeValue,
          variant: this.badgeVariant(typeValue),
        },
        {
          type: 'label',
          value: nameValue,
        },
        {
          type: 'label',
          value: displayValue,
        },
        {
          type: 'checkbox',
        },
        {
          type: 'checkbox',
        },
        {
          type: 'checkbox',
        },
        {
          type: 'checkbox',
        },
        {
          type: 'toggle',
          checked: true,
        },
      ],
    });
    this.notifyContentChanged();

    addFields.forEach((field, fieldIndex) => {
      if (field.type === 'text') {
        this.addFieldState.set(this.addFieldKey(sectionIndex, fieldIndex), '');
      }
    });
  }

  badgeVariant(value: string): string | undefined {
    const normalizedValue = value.toLowerCase();
    if (['numeric', 'number'].includes(normalizedValue)) {
      return 'numeric';
    }

    if (['datetime', 'date', 'array', 'json'].includes(normalizedValue)) {
      return 'date';
    }

    if (['integer', 'int', 'long'].includes(normalizedValue)) {
      return 'version';
    }

    return undefined;
  }
}

